import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  UploadSimple, FileXls, ArrowRight, ArrowLeft, CheckCircle, WarningCircle,
  Warning, ArrowClockwise, CaretRight,
} from "@phosphor-icons/react";
import Layout from "../components/Layout";
import api, { apiErrorMessage } from "../lib/api";
import { fmtCurrency, fmtNumber, fmtDecimal, fmtDate, hasPerm } from "../lib/format";
import { useAuth } from "../context/AuthContext";

const FIELDS = [
  { key: "bill_date", label: "Bill Date", required: true },
  { key: "bill_number", label: "Bill Number", required: false },
  { key: "item_name", label: "Item Name", required: false },
  { key: "category", label: "Category", required: true },
  { key: "quantity", label: "Quantity", required: true },
  { key: "amount", label: "Amount", required: true },
  { key: "floor", label: "Floor", required: true },
  { key: "salesman", label: "Salesman", required: true },
];

const STEPS = ["Upload File", "Map Columns", "Preview", "Done"];

function Stepper({ step }) {
  return (
    <div className="mb-6 flex items-center gap-2 overflow-x-auto">
      {STEPS.map((s, i) => (
        <div key={s} className="flex items-center gap-2">
          <div className={`flex items-center gap-2 whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-semibold ${
            i === step ? "bg-blue-700 text-white" : i < step ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-400"
          }`}>
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white/20 text-[10px]">{i + 1}</span>
            {s}
          </div>
          {i < STEPS.length - 1 && <CaretRight size={14} className="text-slate-300" />}
        </div>
      ))}
    </div>
  );
}

export default function Upload() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canReplace = hasPerm(user, "replace_data");

  const [step, setStep] = useState(0);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [analyze, setAnalyze] = useState(null);
  const [mapping, setMapping] = useState({});
  const [templateName, setTemplateName] = useState("Standard POS Export");
  const [preview, setPreview] = useState(null);
  const [replace, setReplace] = useState(false);
  const [result, setResult] = useState(null);

  const onFile = async (f) => {
    if (!f) return;
    if (!/\.(xlsx|xls)$/i.test(f.name)) {
      toast.error("Please select an .xlsx or .xls file");
      return;
    }
    setFile(f);
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload/analyze", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAnalyze(data);
      const base = data.last_mapping || data.suggested_mapping || {};
      setMapping({ ...base });
      if (data.last_template_name) setTemplateName(data.last_template_name);
      setStep(1);
    } catch (e) {
      toast.error(apiErrorMessage(e.response?.data?.detail) || "Could not read the file");
      setFile(null);
    } finally {
      setBusy(false);
    }
  };

  const runPreview = async () => {
    for (const f of FIELDS.filter((x) => x.required)) {
      if (!mapping[f.key]) {
        toast.error(`Please map the required field: ${f.label}`);
        return;
      }
    }
    setBusy(true);
    try {
      const { data } = await api.post("/upload/preview", {
        mapping, rows: analyze.rows, file_name: analyze.file_name,
      });
      setPreview(data);
      setReplace(false);
      setStep(2);
    } catch (e) {
      toast.error(apiErrorMessage(e.response?.data?.detail) || "Preview failed");
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/upload/commit", {
        mapping, rows: analyze.rows, file_name: analyze.file_name,
        replace, save_template_name: templateName || undefined,
      });
      setResult(data);
      setStep(3);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(apiErrorMessage(detail) || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setStep(0); setFile(null); setAnalyze(null); setMapping({}); setPreview(null); setResult(null); setReplace(false);
  };

  return (
    <Layout title="Upload Sales">
      <div className="mx-auto max-w-3xl animate-fade-in">
        <Stepper step={step} />

        {/* STEP 0 — file */}
        {step === 0 && (
          <label
            data-testid="file-dropzone"
            className="flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-slate-300 bg-white px-6 py-16 text-center transition-colors hover:border-blue-400 hover:bg-blue-50/40"
          >
            <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-blue-100 text-blue-700">
              {busy ? <ArrowClockwise size={28} className="animate-spin" /> : <UploadSimple size={28} weight="bold" />}
            </div>
            <div className="font-display text-lg font-bold text-slate-800">
              {busy ? "Reading file…" : "Upload daily POS export"}
            </div>
            <p className="mt-1 text-sm text-slate-500">Tap to select an .xlsx or .xls file from your device</p>
            <input
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              data-testid="file-input"
              disabled={busy}
              onChange={(e) => onFile(e.target.files?.[0])}
            />
          </label>
        )}

        {/* STEP 1 — map */}
        {step === 1 && analyze && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 rounded-md bg-slate-100 px-3 py-2 text-sm text-slate-600">
              <FileXls size={18} weight="bold" className="text-emerald-600" /> {analyze.file_name} · {fmtNumber(analyze.row_count)} rows
            </div>
            {analyze.last_template_name && (
              <div className="rounded-md bg-blue-50 px-3 py-2 text-xs text-blue-700">
                Loaded your last-used mapping: <b>{analyze.last_template_name}</b>. Edit below if needed.
              </div>
            )}
            <div className="rounded-lg border border-slate-200 bg-white p-4">
              <h3 className="mb-3 font-display text-base font-bold text-slate-800">Map application fields to Excel columns</h3>
              <div className="space-y-3">
                {FIELDS.map((f) => (
                  <div key={f.key} className="grid grid-cols-1 items-center gap-2 sm:grid-cols-2">
                    <label className="text-sm font-medium text-slate-700">
                      {f.label} {f.required && <span className="text-red-500">*</span>}
                    </label>
                    <select
                      data-testid={`map-${f.key}`}
                      value={mapping[f.key] || ""}
                      onChange={(e) => setMapping((m) => ({ ...m, [f.key]: e.target.value || null }))}
                      className="rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-700 focus:ring-2 focus:ring-blue-700"
                    >
                      <option value="">— Not mapped —</option>
                      {analyze.columns.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-4">
              <label className="mb-1.5 block text-sm font-medium text-slate-700">Save mapping as template</label>
              <input
                data-testid="template-name-input"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-700 focus:ring-2 focus:ring-blue-700"
                placeholder="e.g. Standard POS Export"
              />
              <p className="mt-1 text-xs text-slate-400">This mapping will be remembered and auto-loaded next time.</p>
            </div>
            <div className="flex justify-between">
              <button onClick={reset} className="flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
                <ArrowLeft size={16} /> Back
              </button>
              <button onClick={runPreview} disabled={busy} data-testid="to-preview-btn" className="flex items-center gap-1.5 rounded-md bg-blue-700 px-5 py-2 text-sm font-semibold text-white hover:bg-blue-800 disabled:opacity-60">
                {busy ? "Validating…" : "Preview Import"} <ArrowRight size={16} />
              </button>
            </div>
          </div>
        )}

        {/* STEP 2 — preview */}
        {step === 2 && preview && (
          <div className="space-y-4">
            {preview.errors.length > 0 && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-4" data-testid="preview-errors">
                <div className="mb-2 flex items-center gap-2 font-semibold text-red-700">
                  <WarningCircle size={18} weight="bold" /> Blocking errors — cannot import
                </div>
                <ul className="list-inside list-disc text-sm text-red-600">
                  {preview.errors.map((er, i) => <li key={i}>{er.message}</li>)}
                </ul>
              </div>
            )}

            {preview.multiple_dates && (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
                <Warning size={16} weight="bold" className="mr-1 inline" />
                This file contains <b>multiple business dates</b>. All dates below will be imported.
              </div>
            )}

            <div className="space-y-3">
              {preview.dates.map((d) => {
                const exists = preview.existing_dates.includes(d.date);
                return (
                  <div key={d.date} className="rounded-lg border border-slate-200 bg-white p-4" data-testid={`preview-date-${d.date}`}>
                    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                      <div className="font-display text-lg font-bold text-slate-900">{fmtDate(d.date)}</div>
                      {exists && (
                        <span className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-700">Already exists</span>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                      <Stat label="Rows" value={fmtNumber(d.total_rows)} />
                      <Stat label="Unique Bills" value={fmtNumber(d.unique_bills)} />
                      <Stat label="Sales Qty" value={fmtNumber(d.sales_qty)} />
                      <Stat label="Sales Amount" value={fmtCurrency(d.sales_amount)} />
                      <Stat label="Return Qty" value={fmtNumber(d.return_qty)} />
                      <Stat label="Return Amount" value={fmtCurrency(d.return_amount)} />
                      <Stat label="Floors" value={fmtNumber(d.floors.length)} />
                      <Stat label="Categories" value={fmtNumber((d.categories || []).length)} />
                      <Stat label="Salesmen" value={fmtNumber(d.salesmen.length)} />
                    </div>
                  </div>
                );
              })}
            </div>

            {preview.warnings.length > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4" data-testid="preview-warnings">
                <div className="mb-2 flex items-center gap-2 font-semibold text-amber-700">
                  <Warning size={18} weight="bold" /> Warnings ({preview.warnings.length})
                </div>
                <ul className="space-y-1 text-sm text-amber-700">
                  {preview.warnings.map((w, i) => (
                    <li key={i}>• {w.message} <b>({w.count})</b></li>
                  ))}
                </ul>
                <p className="mt-2 text-xs text-amber-600">Warnings do not block import. Review before confirming.</p>
              </div>
            )}

            {preview.existing_dates.length > 0 && preview.errors.length === 0 && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 p-4" data-testid="existing-date-warning">
                <div className="font-semibold text-amber-800">
                  Sales data already exists for {preview.existing_dates.map(fmtDate).join(", ")}.
                </div>
                {canReplace ? (
                  <label className="mt-3 flex items-center gap-2 text-sm text-amber-800">
                    <input type="checkbox" data-testid="replace-checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)} className="h-4 w-4" />
                    Replace the existing data for these dates (existing records will be safely replaced and recalculated).
                  </label>
                ) : (
                  <p className="mt-2 text-sm text-amber-700">You do not have permission to replace existing data. Please contact an administrator.</p>
                )}
              </div>
            )}

            <div className="flex justify-between">
              <button onClick={() => setStep(1)} className="flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
                <ArrowLeft size={16} /> Back
              </button>
              <button
                onClick={commit}
                data-testid="confirm-import-btn"
                disabled={busy || preview.errors.length > 0 || (preview.existing_dates.length > 0 && (!canReplace || !replace))}
                className="flex items-center gap-1.5 rounded-md bg-blue-700 px-5 py-2 text-sm font-semibold text-white hover:bg-blue-800 disabled:opacity-50"
              >
                {busy ? "Importing…" : replace ? "Replace & Import" : "Confirm Import"} <CheckCircle size={16} weight="bold" />
              </button>
            </div>
          </div>
        )}

        {/* STEP 3 — done */}
        {step === 3 && result && (
          <div className="rounded-lg border border-emerald-200 bg-white p-6 text-center" data-testid="upload-success">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
              <CheckCircle size={36} weight="fill" />
            </div>
            <h2 className="font-display text-2xl font-extrabold tracking-tight text-slate-900">Sales Data Imported Successfully</h2>
            <p className="mt-1 text-sm text-slate-500">{result.dates.map(fmtDate).join(", ")}{result.replaced ? " · replaced existing data" : ""}</p>
            <div className="mx-auto mt-6 grid max-w-md grid-cols-2 gap-3 text-left">
              <Stat label="Bills" value={fmtNumber(result.bills)} />
              <Stat label="Items" value={fmtNumber(result.items)} />
              <Stat label="Sales" value={fmtCurrency(result.sales_amount)} />
              <Stat label="Returns" value={fmtCurrency(result.return_amount)} />
              <Stat label="Net Sales" value={fmtCurrency(result.net_sales)} />
              <Stat label="ABV" value={fmtCurrency(result.abv, true)} />
              <Stat label="ABQ" value={fmtDecimal(result.abq)} />
            </div>
            <div className="mt-8 flex justify-center gap-3">
              <button onClick={reset} data-testid="upload-another-btn" className="rounded-md border border-slate-300 bg-white px-5 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50">
                Upload Another
              </button>
              <button onClick={() => navigate("/")} data-testid="view-dashboard-btn" className="rounded-md bg-blue-700 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-800">
                View Dashboard
              </button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-md border border-slate-100 bg-slate-50 px-3 py-2">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-0.5 font-mono-data text-sm font-semibold text-slate-900">{value}</div>
    </div>
  );
}
